import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Zap, FileText, Search, ArrowLeft, Info, Loader2, UserCircle, Settings, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
    <div className="flex items-center space-x-3 mb-6">
      <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-[#256df3]">
        {Icon && <Icon size={18} />}
      </div>
      <h3 className="text-lg font-bold text-gray-800">{title}</h3>
    </div>
    {children}
  </div>
);

const SelectableTag = ({ label, selected, onClick }) => (
  <button 
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
      selected 
      ? 'bg-[#256df3] border-[#256df3] text-white shadow-md shadow-blue-200' 
      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
    }`}
  >
    {label}
  </button>
);

export default function GenerateConfig() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode");
  const { user, token } = useAuth();
  
  // State
  const [selectedTheme, setSelectedTheme] = useState(''); 
  const [wordCount, setWordCount] = useState([500, 800]);
  const [articleCount, setArticleCount] = useState(5);
  const [selectionMode, setSelectionMode] = useState('interaction'); 
  const [analyzeCover, setAnalyzeCover] = useState(true);
  const [personaId, setPersonaId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(true);
  const [showPersonas, setShowPersonas] = useState(false);
  const [showRecommendedThemes, setShowRecommendedThemes] = useState(false);
  const [useDeepThinking, setUseDeepThinking] = useState(true);
  const [removeAIFlavor, setRemoveAIFlavor] = useState(true);
  const [antiRoutineMode, setAntiRoutineMode] = useState(true);
          mode: mode || "normal"
  
  // Pricing State with safe defaults
  const [pricing, setPricing] = useState({ 
    COST_PER_GENERATION: 0, 
    COST_PER_IMAGE: 0 
  });

  // Fetch Pricing safely
  useEffect(() => {
    let isMounted = true;

    const fetchPricing = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API_URL}/config/pricing`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (isMounted) {
          if (res.status === 401) {
             navigate('/login');
             return;
          }
          setPricing(res.data || { COST_PER_GENERATION: 0, COST_PER_IMAGE: 0 });
        }
      } catch (e) {
        console.warn("Pricing fetch failed", e);
        // Do not crash, just keep defaults
      }
    };

    fetchPricing();

    return () => { isMounted = false; };
  }, [token, navigate]);

  // Calculate Total Cost with Safety Checks
  const genCost = Number(pricing?.COST_PER_GENERATION) || 0;
  const imgCost = Number(pricing?.COST_PER_IMAGE) || 0;
  const totalCost = articleCount * (genCost + (analyzeCover ? imgCost : 0));

  const themes = ['美妆护肤', '母婴育儿', '穿搭时尚', '家居生活', '美食饮品', '健康养生', '宠物', '户外旅行', '学习教育', '本地生活', '数码科技', '情感疗愈', '新中式国风'];

  const handleNext = async () => {
    try {
      setIsSubmitting(true);
      await axios.patch(`${API_URL}/tasks/${id}`, {
        status: 'GENERATING',
        config: {
          themes: [selectedTheme],
          wordCount,
          articleCount,
          selectionMode,
          analyzeCover,
          persona_id: personaId,
          useDeepThinking,
          removeAIFlavor,
          antiRoutineMode,
          mode: mode || "normal"
        }
      }, {
        headers: { 'x-user-email': user?.email }
      });
      navigate('/tasks');
    } catch (error) {
      console.error("Failed to start generation:", error);
      alert("启动生成失败，请重试");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto custom-scrollbar pb-20">
        <div className="container mx-auto max-w-4xl">
          {/* Header */}
          <header className="flex items-center justify-between mb-8 sticky top-0 z-30 pt-4 pb-2 bg-gray-50/95 backdrop-blur-sm">
            <div>
              <div className="flex items-center space-x-2 text-sm text-gray-500 mb-1">
                <span>任务 #{id}</span>
                <span>/</span>
                <span className="text-[#256df3] font-medium">第 3 步：配置参数</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">AI 生成参数设置</h1>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center px-3 py-1 bg-amber-50 text-amber-600 rounded-lg text-xs font-medium border border-amber-100">
                <Zap size={14} className="mr-1.5" />
                <span>预计消耗 {totalCost} 点数</span>
              </div>
            </div>
          </header>

          {/* Form Sections */}
          <div className="space-y-6">
            {/* 1. Theme Selection */}
            
            {/* Persona Selection */}
            <div className="mb-6">
              <button
                type="button"
                onClick={() => setShowPersonas(!showPersonas)}
                className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors bg-white px-4 py-2 rounded-xl border border-gray-200 shadow-sm"
              >
                <UserCircle size={16} className="mr-2" />
                选择生成人设 (默认智能推断)
                {showPersonas ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
              </button>
              
              {showPersonas && (
                <div className="mt-4">
                  <Section title="选择生成人设" icon={UserCircle}>
                    <p className="text-sm text-gray-500 mb-4">您可以强制指定生成文章的作者身份语气，或由 AI 智能推断。</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                      <div 
                        onClick={() => setPersonaId('')}
                        className={`cursor-pointer border p-4 rounded-xl transition-all ${personaId === '' ? 'border-pink-500 bg-pink-50 ring-1 ring-pink-500' : 'border-gray-200 hover:border-pink-300'}`}
                      >
                        <div className="font-medium text-gray-900 mb-1">🤖 智能推断 (推荐)</div>
                        <div className="text-xs text-gray-500">仿写时根据种子文章自动推断爆款作者的真实人设</div>
                      </div>
                      <div 
                        className="cursor-not-allowed border p-4 rounded-xl transition-all border-gray-200 bg-gray-50 opacity-60 grayscale"
                        title="暂未开放"
                      >
                        <div className="font-medium text-gray-500 mb-1">🍠 小红书通用人设 (暂未开放)</div>
                        <div className="text-xs text-gray-400">网感好，自然亲切，带有一点种草和分享欲的经典风格</div>
                      </div>
                      <div 
                        onClick={() => setPersonaId('默认通用')}
                        className={`cursor-pointer border p-4 rounded-xl transition-all ${personaId === '默认通用' ? 'border-pink-500 bg-pink-50 ring-1 ring-pink-500' : 'border-gray-200 hover:border-pink-300'}`}
                      >
                        <div className="font-medium text-gray-900 mb-1">👤 默认通用博主</div>
                        <div className="text-xs text-gray-500">真实诚恳的消费者分享视角，自然中性口吻</div>
                      </div>
                      <div 
                        onClick={() => setPersonaId('成分党')}
                        className={`cursor-pointer border p-4 rounded-xl transition-all ${personaId === '成分党' ? 'border-pink-500 bg-pink-50 ring-1 ring-pink-500' : 'border-gray-200 hover:border-pink-300'}`}
                      >
                        <div className="font-medium text-gray-900 mb-1">🔬 成分护肤狂</div>
                        <div className="text-xs text-gray-500">客观严谨，用数据和成分说话的理性玩家</div>
                      </div>
                      <div 
                        onClick={() => setPersonaId('学生党')}
                        className={`cursor-pointer border p-4 rounded-xl transition-all ${personaId === '学生党' ? 'border-pink-500 bg-pink-50 ring-1 ring-pink-500' : 'border-gray-200 hover:border-pink-300'}`}
                      >
                        <div className="font-medium text-gray-900 mb-1">🎒 羊毛学生党</div>
                        <div className="text-xs text-gray-500">预算有限，追求性价比的精明买手</div>
                      </div>
                      <div 
                        onClick={() => setPersonaId('贵妇')}
                        className={`cursor-pointer border p-4 rounded-xl transition-all ${personaId === '贵妇' ? 'border-pink-500 bg-pink-50 ring-1 ring-pink-500' : 'border-gray-200 hover:border-pink-300'}`}
                      >
                        <div className="font-medium text-gray-900 mb-1">👑 挑剔贵妇</div>
                        <div className="text-xs text-gray-500">追求极致护肤体验的高端玩家</div>
                      </div>
                    </div>
                  </Section>
                </div>
              )}
            </div>

            <Section title="内容生成主题" icon={FileText}>
              <p className="text-sm text-gray-500 mb-4">请输入或选择一个主题，生成的内容将围绕该主题展开。</p>
              
              <input 
                type="text" 
                placeholder="请输入您想要生成的主题内容..." 
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                className="w-full px-4 py-3 mb-4 bg-white border border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-[#256df3] transition-all shadow-sm"
              />

              <div className="mb-2">
                <button
                  type="button"
                  onClick={() => setShowRecommendedThemes(!showRecommendedThemes)}
                  className="flex items-center text-sm font-medium text-[#256df3] hover:text-blue-700 transition-colors"
                >
                  {showRecommendedThemes ? '收起推荐主题' : '展开推荐主题'}
                  {showRecommendedThemes ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
                </button>
              </div>

              {showRecommendedThemes && (
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                  {themes.map(theme => (
                    <SelectableTag 
                      key={theme} 
                      label={theme} 
                      selected={selectedTheme === theme} 
                      onClick={() => setSelectedTheme(theme)}
                    />
                  ))}
                </div>
              )}
            </Section>

            {/* 2. Text Requirements */}
            <Section title="文字需求" icon={FileText}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-3">文章字数范围</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div 
                      onClick={() => setWordCount([300, 500])}
                      className={`cursor-pointer border p-4 rounded-xl transition-all ${wordCount[0] === 300 && wordCount[1] === 500 ? 'border-[#256df3] bg-blue-50 ring-1 ring-[#256df3]' : 'border-gray-200 hover:border-blue-300'}`}
                    >
                      <div className="font-medium text-gray-900 mb-1 flex items-center"><span className="mr-1">💡</span> 短文 (300-500字)</div>
                      <div className="text-xs text-gray-500">适合种草、日常分享</div>
                    </div>
                    <div 
                      onClick={() => setWordCount([500, 800])}
                      className={`cursor-pointer border p-4 rounded-xl transition-all ${wordCount[0] === 500 && wordCount[1] === 800 ? 'border-[#256df3] bg-blue-50 ring-1 ring-[#256df3]' : 'border-gray-200 hover:border-blue-300'}`}
                    >
                      <div className="font-medium text-gray-900 mb-1 flex items-center"><span className="mr-1">📝</span> 中篇 (500-800字)</div>
                      <div className="text-xs text-gray-500">适合测评、经验分享</div>
                    </div>
                    <div 
                      onClick={() => setWordCount([800, 1200])}
                      className={`cursor-pointer border p-4 rounded-xl transition-all ${wordCount[0] === 800 && wordCount[1] === 1200 ? 'border-[#256df3] bg-blue-50 ring-1 ring-[#256df3]' : 'border-gray-200 hover:border-blue-300'}`}
                    >
                      <div className="font-medium text-gray-900 mb-1 flex items-center"><span className="mr-1">📖</span> 长文 (800-1200字)</div>
                      <div className="text-xs text-gray-500">适合干货、深度评测</div>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">生成文章数量</label>
                  <div className="flex items-center space-x-4">
                    <input 
                      type="range" 
                      min="1" max="20" 
                      value={articleCount} 
                      onChange={(e) => setArticleCount(parseInt(e.target.value) || 1)}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#256df3]"
                    />
                    <span className="w-12 text-center font-bold text-[#256df3] text-lg">{articleCount}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">单次最多生成 20 篇</p>
                </div>
              </div>
            </Section>

            {/* 3. Selection & Analysis */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Section title="挑选机制" icon={Zap}>
                <div className="space-y-4">
                  <label className={`flex items-center p-4 rounded-xl border cursor-pointer transition-all ${selectionMode === 'interaction' ? 'border-[#256df3] bg-blue-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input 
                      type="radio" 
                      name="selection" 
                      className="sr-only" 
                      checked={selectionMode === 'interaction'}
                      onChange={() => setSelectionMode('interaction')}
                    />
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center mr-3 ${selectionMode === 'interaction' ? 'border-[#256df3]' : 'border-gray-300'}`}>
                      {selectionMode === 'interaction' && <div className="w-2.5 h-2.5 rounded-full bg-[#256df3]"></div>}
                    </div>
                    <div>
                      <span className="block text-sm font-medium text-gray-800">高互动优先</span>
                      <span className="block text-xs text-gray-500 mt-0.5">优先选择点赞、评论数据高的内容作为素材</span>
                    </div>
                  </label>

                  <label className={`flex items-center p-4 rounded-xl border cursor-pointer transition-all ${selectionMode === 'random' ? 'border-[#256df3] bg-blue-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input 
                      type="radio" 
                      name="selection" 
                      className="sr-only"
                      checked={selectionMode === 'random'}
                      onChange={() => setSelectionMode('random')}
                    />
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center mr-3 ${selectionMode === 'random' ? 'border-[#256df3]' : 'border-gray-300'}`}>
                      {selectionMode === 'random' && <div className="w-2.5 h-2.5 rounded-full bg-[#256df3]"></div>}
                    </div>
                    <div>
                      <span className="block text-sm font-medium text-gray-800">随机挑选</span>
                      <span className="block text-xs text-gray-500 mt-0.5">从抓取池中随机组合内容，增加多样性</span>
                    </div>
                  </label>
                </div>
              </Section>

              <Section title="图片分析" icon={Search}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-800">封面头图分析</h4>
                    <p className="text-xs text-gray-500 mt-1">分析选中内容的封面特征，生成相似风格的配图</p>
                  </div>
                  <div className="relative inline-block w-12 h-6 align-middle select-none transition duration-200 ease-in">
                    <input 
                      type="checkbox" 
                      name="toggle" 
                      id="toggle" 
                      className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer transition-all duration-300 right-0 border-[#256df3]" 
                      checked={analyzeCover}
                      onChange={() => setAnalyzeCover(!analyzeCover)}
                      style={{ right: analyzeCover ? '0' : 'auto', left: analyzeCover ? 'auto' : '0', borderColor: analyzeCover ? '#256df3' : '#e5e7eb' }}
                    />
                    <label 
                      htmlFor="toggle" 
                      className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-300 ${analyzeCover ? 'bg-[#256df3]' : 'bg-gray-300'}`}
                    ></label>
                  </div>
                </div>
                <div className={`p-4 bg-blue-50 rounded-xl border border-blue-100 transition-opacity ${analyzeCover ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                  <div className="flex items-start space-x-2">
                    <Info size={16} className="text-[#256df3] mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-800 leading-relaxed">
                      开启后，AI 将提取封面中的配色、构图和文字排版风格，并在后续步骤中为您制作封面头图，如果不开启将不生成图片。
                    </p>
                  </div>
                </div>
              </Section>
            </div>


            {/* 4. Advanced Settings */}
            <div className="mb-6">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors bg-white px-4 py-2 rounded-xl border border-gray-200 shadow-sm"
              >
                <Settings size={16} className="mr-2" />
                高级生成设置
                {showAdvanced ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
              </button>
              
              {showAdvanced && (
                <div className="mt-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="pr-8">
                      <h4 className="text-sm font-medium text-gray-800 flex items-center">
                        <Zap size={16} className="text-amber-500 mr-1.5" />
                        深度推理模式 (两步流水线)
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">先生成详尽大纲，再基于大纲进行扩写。生成质量更高、逻辑更严密，但耗时稍长。</p>
                    </div>
                    <div className="relative inline-block w-12 h-6 align-middle select-none transition duration-200 ease-in flex-shrink-0">
                      <input 
                        type="checkbox" 
                        id="toggle-deep" 
                        className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer transition-all duration-300 right-0 border-[#256df3]" 
                        checked={useDeepThinking}
                        onChange={() => setUseDeepThinking(!useDeepThinking)}
                        style={{ right: useDeepThinking ? '0' : 'auto', left: useDeepThinking ? 'auto' : '0', borderColor: useDeepThinking ? '#256df3' : '#e5e7eb' }}
                      />
                      <label 
                        htmlFor="toggle-deep" 
                        className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-300 ${useDeepThinking ? 'bg-[#256df3]' : 'bg-gray-300'}`}
                      ></label>
                    </div>
                  </div>
                  
                  <div className="w-full h-px bg-gray-100"></div>

                  <div className="flex items-center justify-between">
                    <div className="pr-8">
                      <h4 className="text-sm font-medium text-gray-800 flex items-center">
                        <Search size={16} className="text-green-500 mr-1.5" />
                        强力去 AI 味
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">严格的词频拦截与机器惯用语（如“家人们”、“综上所述”）强制替换，提升拟真度。</p>
                    </div>
                    <div className="relative inline-block w-12 h-6 align-middle select-none transition duration-200 ease-in flex-shrink-0">
                      <input 
                        type="checkbox" 
                        id="toggle-flavor" 
                        className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer transition-all duration-300 right-0 border-[#256df3]" 
                        checked={removeAIFlavor}
                        onChange={() => setRemoveAIFlavor(!removeAIFlavor)}
                        style={{ right: removeAIFlavor ? '0' : 'auto', left: removeAIFlavor ? 'auto' : '0', borderColor: removeAIFlavor ? '#256df3' : '#e5e7eb' }}
                      />
                      <label 
                        htmlFor="toggle-flavor" 
                        className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-300 ${removeAIFlavor ? 'bg-[#256df3]' : 'bg-gray-300'}`}
                      ></label>
                    </div>
                  </div>
                <div className="w-full h-px bg-gray-100"></div>

                  <div className="flex items-center justify-between">
                    <div className="pr-8">
                      <h4 className="text-sm font-medium text-gray-800 flex items-center">
                        <Sparkles size={16} className="text-indigo-500 mr-1.5" />
                        反套路真实风
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">开启后，文章将采用冷静克制、拒绝夸张套路、且包含真实缺点摩擦的叙事风格。</p>
                    </div>
                    <div className="relative inline-block w-12 h-6 align-middle select-none transition duration-200 ease-in flex-shrink-0">
                      <input 
                        type="checkbox" 
                        id="toggle-anti-routine" 
                        className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer transition-all duration-300 right-0 border-[#256df3]" 
                        checked={antiRoutineMode}
                        onChange={() => setAntiRoutineMode(!antiRoutineMode)}
                        style={{ right: antiRoutineMode ? '0' : 'auto', left: antiRoutineMode ? 'auto' : '0', borderColor: antiRoutineMode ? '#256df3' : '#e5e7eb' }}
                      />
                      <label 
                        htmlFor="toggle-anti-routine" 
                        className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-300 ${antiRoutineMode ? 'bg-[#256df3]' : 'bg-gray-300'}`}
                      ></label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 md:left-64 right-0 p-4 bg-white border-t border-gray-100 flex items-center justify-between z-40">
        <button 
          onClick={handleBack}
          disabled={isSubmitting}
          className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2 disabled:opacity-50"
        >
          <ArrowLeft size={18} />
          <span>上一步</span>
        </button>
        <button 
          onClick={handleNext}
          disabled={isSubmitting}
          className="px-8 py-2.5 bg-gradient-to-r from-[#256df3] to-[#37a7f3] text-white rounded-xl shadow-lg shadow-blue-200 font-bold hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5 flex items-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Zap size={18} />}
          <span>{isSubmitting ? '提交请求中...' : '开始生成文章'}</span>
        </button>
      </div>
    </div>
  );
}
